import { BolsoLayout } from "@/components/BolsoLayout";
import { TransactionForm } from "@/components/TransactionForm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { AlertTriangle, Loader2, PiggyBank, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";

export default function Budgets() {
  const { t, ui, formatCurrency, currencySymbol, categoryName } = useI18n();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [showForm, setShowTxForm] = useState(false);
  const [showBudgetForm, setShowBudgetForm] = useState(false);
  const [categoryId, setCategoryId] = useState("");
  const [limitAmount, setLimitAmount] = useState("");

  const { data: budgets = [], isLoading } = trpc.budgets.list.useQuery({ month, year });
  const { data: categories = [] } = trpc.categories.list.useQuery({});
  const utils = trpc.useUtils();

  const upsertMutation = trpc.budgets.upsert.useMutation({
    onSuccess: () => {
      toast.success(ui.budgets.saved);
      utils.budgets.list.invalidate();
      setShowBudgetForm(false);
      setCategoryId("");
      setLimitAmount("");
    },
    onError: (e) => toast.error(e.message),
  });

  const deleteMutation = trpc.budgets.delete.useMutation({
    onSuccess: () => { toast.success(ui.budgets.removed); utils.budgets.list.invalidate(); },
    onError: () => toast.error(ui.budgets.removeError),
  });

  const handleSave = () => {
    const limit = parseFloat(limitAmount.replace(",", "."));
    if (!categoryId || !limit) return toast.error(ui.budgets.required);
    upsertMutation.mutate({ categoryId: parseInt(categoryId), limitAmount: limit, month, year });
  };

  const expenseCategories = categories.filter(
    (c) => !["Salário", "Freelance", "Investimentos"].includes(c.name)
  );

  const monthNames = t.common.months.map((m: string) => m.slice(0, 3));

  return (
    <BolsoLayout onNewTransaction={() => setShowTxForm(true)}>
      <div className="p-4 lg:p-6 max-w-4xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="text-2xl font-serif font-bold text-foreground">{t.budgets.title}</h1>
          <div className="flex items-center gap-2">
            <Select value={`${month}-${year}`} onValueChange={(v) => {
              const [m, y] = v.split("-").map(Number);
              setMonth(m); setYear(y);
            }}>
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 12 }, (_, i) => {
                  const d = new Date(now.getFullYear(), now.getMonth() - 5 + i, 1);
                  return (
                    <SelectItem key={i} value={`${d.getMonth() + 1}-${d.getFullYear()}`}>
                      {monthNames[d.getMonth()]} {d.getFullYear()}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <Button onClick={() => setShowBudgetForm(true)} size="sm" className="gap-1.5">
              <Plus className="w-4 h-4" /> {t.budgets.new}
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
          </div>
        ) : budgets.length === 0 ? (
          <Card className="border-border shadow-sm">
            <CardContent className="p-10 text-center text-muted-foreground">
              <PiggyBank className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">{t.budgets.noBudgets}</p>
              <p className="text-sm mt-1">{t.budgets.createFirst}</p>
              <Button variant="outline" size="sm" className="mt-4" onClick={() => setShowBudgetForm(true)}>
                <Plus className="w-4 h-4 mr-1" /> {t.budgets.new}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {budgets.map((budget) => {
              const cat = categories.find((c) => c.id === budget.categoryId);
              const pct = budget.percentage ?? 0;
              const isOver = pct >= 100;
              const isWarning = pct >= 80 && pct < 100;
              return (
                <Card key={budget.id} className={cn(
                  "border-border shadow-sm transition-all",
                  isOver && "border-destructive/50",
                  isWarning && "border-yellow-400/50"
                )}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                          <span className="text-sm font-bold text-primary">
                            {cat?.name?.charAt(0) ?? "?"}
                          </span>
                        </div>
                        <div>
                          <p className="font-semibold text-foreground text-sm">{categoryName(cat?.name) || ui.common.noCategory}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatCurrency(budget.spent ?? 0)} {t.budgets.of} {formatCurrency(parseFloat(String(budget.limitAmount)))}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {isOver && (
                          <Badge variant="destructive" className="text-xs gap-1">
                            <AlertTriangle className="w-3 h-3" /> {t.budgets.alert100}
                          </Badge>
                        )}
                        {isWarning && (
                          <Badge className="text-xs gap-1 bg-yellow-100 text-yellow-800 border-yellow-300">
                            <AlertTriangle className="w-3 h-3" /> {t.budgets.alert80}
                          </Badge>
                        )}
                        <button
                          onClick={() => deleteMutation.mutate({ id: budget.id })}
                          className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    <div>
                      <Progress
                        value={Math.min(pct, 100)}
                        className={cn("h-2", isOver && "[&>div]:bg-destructive", isWarning && "[&>div]:bg-yellow-500")}
                      />
                      <p className={cn(
                        "text-xs text-right mt-1 font-medium",
                        isOver ? "text-destructive" : isWarning ? "text-yellow-600" : "text-muted-foreground"
                      )}>
                        {pct.toFixed(0)}% {t.budgets.used}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Budget form dialog */}
      <Dialog open={showBudgetForm} onOpenChange={(v) => !v && setShowBudgetForm(false)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">{t.budgets.new}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">{t.budgets.category}</Label>
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger>
                  <SelectValue placeholder={ui.budgets.selectCategory} />
                </SelectTrigger>
                <SelectContent>
                  {expenseCategories.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>{categoryName(c.name)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">{ui.budgets.monthlyLimit} ({currencySymbol})</Label>
              <Input
                type="number"
                placeholder={ui.budgets.limitPlaceholder}
                value={limitAmount}
                onChange={(e) => setLimitAmount(e.target.value)}
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowBudgetForm(false)}>{t.budgets.cancel}</Button>
              <Button className="flex-1" onClick={handleSave} disabled={upsertMutation.isPending}>
                {upsertMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {t.budgets.save}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <TransactionForm open={showForm} onClose={() => setShowTxForm(false)} />
    </BolsoLayout>
  );
}
